import React, { useState } from 'react';

import { endOfDay, endOfWeek, startOfDay, startOfWeek, subDays, subWeeks } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { formatDate } from 'lib/helpers/dateUtils';
import { cn, ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';

import Button from '../Button/Button';
import DatePicker from '../DatePicker/DatePicker';
import Icon from '../Icon/Icon';
import Input from '../Input/Input';
import Popover from '../Popovers/Popover/Popover';
import Typography from '../Typography/Typography';

export function getDateRange(unit, amount, timeZone) {
  const now = timeZone ? toZonedTime(new Date(), timeZone) : new Date();
  let startDate;
  if (unit === 'days') {
    startDate = startOfDay(subDays(now, amount)).toISOString();
  } else if (unit === 'weeks') {
    startDate = startOfDay(subWeeks(now, amount)).toISOString();
  } else if (unit === 'months') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - amount);
    startDate = startOfDay(d).toISOString();
  } else {
    startDate = startOfDay(subDays(now, amount)).toISOString();
  }
  const endDate = endOfDay(now).toISOString();
  return { startDate, endDate };
}

export function getDateRangeByUnit(unit, amount, timeZone) {
  const now = timeZone ? toZonedTime(new Date(), timeZone) : new Date();
  let shifted;
  if (unit === 'days') {
    shifted = subDays(now, amount);
  } else if (unit === 'weeks') {
    shifted = subWeeks(now, amount);
  } else {
    shifted = new Date(now);
    shifted.setMonth(shifted.getMonth() - amount);
  }
  let startDate, endDate;
  if (unit === 'weeks') {
    startDate = startOfWeek(shifted).toISOString();
    endDate = endOfWeek(shifted).toISOString();
  } else {
    startDate = startOfDay(shifted).toISOString();
    endDate = endOfDay(shifted).toISOString();
  }
  return { startDate, endDate };
}

export const getDateOptions = (timeZone) => [
  { id: 'all-time', label: 'All Time', value: null },
  { id: 'today', label: 'Today', value: getDateRangeByUnit('days', 0, timeZone) },
  { id: 'yesterday', label: 'Yesterday', value: getDateRangeByUnit('days', 1, timeZone) },
  { id: 'last-week', label: 'Last Week', value: getDateRangeByUnit('weeks', 1, timeZone) },
  { id: 'last-30-days', label: 'Last 30 Days', value: getDateRange('days', 30, timeZone) },
  { id: 'last-60-days', label: 'Last 60 Days', value: getDateRange('days', 60, timeZone) },
  { id: 'last-90-days', label: 'Last 90 Days', value: getDateRange('days', 90, timeZone) },
  {
    id: 'custom-date-range',
    label: 'Custom date range',
    value: null
  }
];

const Option = ({ label, value, onClick, option, selectedOption }) => {
  return (
    <div
      className="flex h-8 cursor-pointer items-center justify-between px-4 hover:bg-primary-50"
      onClick={() => onClick(option)}>
      <Typography className="cursor-pointer whitespace-nowrap" size="small">
        {label}
      </Typography>
      {selectedOption?.value === value && <Icon icon="check-linear" color="neutral" shadow={700} />}
    </div>
  );
};

export const DateRangeContent = ({
  onChange,
  value,
  onClose = () => {},
  className,
  dataKey,
  formula
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState('');
  const timezone = useTimezone();

  const handleChange = (event) => {
    const value = event.target.value;
    setInput(value);

    const number = parseInt(value, 10);

    if (!isNaN(number)) {
      setSuggestions([
        {
          label: number === 1 ? 'Yesterday' : `Last ${number} Days`,
          value: getDateRange('days', number)
        },
        {
          label: number === 1 ? 'Last Week' : `Last ${number} Weeks`,
          value: getDateRange('weeks', number)
        },
        {
          label: number === 1 ? 'Last Month' : `Last ${number} Months`,
          value: getDateRange('months', number)
        }
      ]);
    } else {
      setSuggestions([]);
    }
  };

  const onSelectOptionHandler = (option, closePopover) => {
    setInput('');
    setSuggestions([]);
    if (!option.value) {
      onChange(
        { id: option.id, startDate: null, endDate: null, timezone, label: option.label },
        dataKey
      );
      closePopover();
      return;
    }
    onChange({ id: option.id, ...option.value, label: option.label, timezone }, dataKey, formula);
    closePopover();
  };

  const onRangeChange = (ranges) => {
    if (!ranges?.[0] || !ranges?.[1]) return;
    const [startDate, endDate] = ranges;
    onChange({ id: 'custom-date-range', startDate, endDate, timezone }, dataKey, formula);
    onClose();
  };

  const toRenderOptions = ia(suggestions)
    ? suggestions
    : getDateOptions(timezone).filter((o) => o.id !== 'custom-date-range');

  return (
    <div className={className}>
      <div className="flex flex-col gap-y-2">
        <Input
          inputWrapperClassName="w-full border-0 border-b border-neutral-200 rounded-none"
          placeholder="Custom range: 2d, 4d, 8weeks..."
          value={input}
          onChange={handleChange}
        />
        {toRenderOptions.map((option) => (
          <Option
            key={option.id}
            label={option.label}
            value={option.value}
            onClick={() => onSelectOptionHandler(option, onClose)}
            selectedOption={value}
          />
        ))}
        <DatePicker
          isDateRange
          isTimeRange
          isTimezone
          value={value?.startDate && value?.endDate ? [value.startDate, value.endDate] : null}
          onChange={onRangeChange}
          customButtonTrigger={
            <div className="flex h-8 cursor-pointer items-center justify-between px-4 hover:bg-primary-50">
              <Typography className="cursor-pointer whitespace-nowrap" size="small">
                Custom range
              </Typography>
            </div>
          }
        />
      </div>
    </div>
  );
};

const DateRangeSelector = ({
  onChange,
  value,
  buttonClassName,
  buttonTextClassName,
  buttonIcon,
  buttonIconColor = 'neutral',
  position
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const timezone = useTimezone();

  const buttonText =
    value?.id !== 'custom-date-range' ? (
      getDateOptions(timezone).find((option) => option.id === value?.id)?.label ||
      value?.label ||
      'All Time'
    ) : (
      <>
        <label className={cn('cursor-pointer', buttonTextClassName || 'text-neutral-800')}>
          {formatDate(value?.startDate, timezone)}
        </label>
        <span className={cn('cursor-pointer', buttonTextClassName || 'text-neutral-800')}>-</span>
        <label className={cn('cursor-pointer', buttonTextClassName || 'text-neutral-800')}>
          {formatDate(value?.endDate, timezone)}
        </label>
      </>
    );

  return (
    <Popover
      panelClassName="p-0 min-w-[244px] w-fit"
      position={position}
      isFixed
      customButtonTrigger={({ open }) => (
        <Button
          variant="tertiary"
          size="xs"
          text={buttonText}
          icon={buttonIcon}
          className={cn('whitespace-nowrap', open && 'bg-neutral-100', buttonClassName)}
          textClassName={buttonTextClassName}
          onClick={() => setIsOpen(false)}
          iconColor={buttonIconColor}
        />
      )}>
      {({ closePopover }) => (
        <DateRangeContent onChange={onChange} value={value} onClose={closePopover} />
      )}
    </Popover>
  );
};

export default DateRangeSelector;
